import { Alert, Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import Iconify from "../../../minimals/components/iconify";
import { useUserContext } from "../../contexts/UserContext";
import { exportTableToCsv } from "../../taskUtils";
import { fCurrency } from "../../utils/formatNumber";
import { canManageDeadChecks } from "../canManageDeadChecks";
import { columns } from "./columns";
import { DeadCheckInfo } from "./types";

export default function DeadChecks() {
  const { user } = useUserContext();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const to = searchParams.get("to");
  const [checks, setChecks] = useState<DeadCheckInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCheck, setSelectedCheck] = useState<DeadCheckInfo | null>(null);
  const [reviving, setReviving] = useState(false);
  const [reviveError, setReviveError] = useState("");
  const canRevive = canManageDeadChecks(user);

  const fetchChecks = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get<DeadCheckInfo[]>("/api/OutstandingChecks/GetDeadChecks", {
        params: { from, to }, headers: { "x-suppress-global-error": "true" }
      });
      setChecks(data);
    } catch {
      setError("Unable to load dead checks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChecks(); }, [from, to]);

  const revive = async () => {
    if (!selectedCheck) return;
    setReviving(true);
    setReviveError("");
    try {
      await axios.post("/api/OutstandingChecks/ReviveCheck", null, {
        params: { uniqueIdentifier: selectedCheck.uniqueIdentifier }, headers: { "x-suppress-global-error": "true" }
      });
    } catch (error) {
      setReviveError(axios.isAxiosError(error) && typeof error.response?.data === "string"
        ? error.response.data : "Unable to revive this check. Please try again.");
      setReviving(false);
      return;
    }
    setChecks((current) => current.filter((check) => check.uniqueIdentifier !== selectedCheck.uniqueIdentifier));
    setSelectedCheck(null);
    setReviving(false);
    await fetchChecks();
  };

  return (
    <>
      <Helmet><title>Dead Checks | TitleQ</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h4">Dead Checks</Typography>
          <Button component={RouterLink} to="/checks">Outstanding checks</Button>
        </Stack>
        {(from || to) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">Check dates: {from || "Any"} to {to || "Any"}</Typography>
            <Button component={RouterLink} to="/checks/dead" size="small">Show all dead checks</Button>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <MaterialReactTable
          columns={columns}
          data={checks}
          enableStickyHeader
          enableFacetedValues
          enableRowActions={canRevive}
          autoResetPageIndex={false}
          initialState={{
            density: "compact",
            sorting: [{ id: "markedDead", desc: true }],
            pagination: { pageIndex: 0, pageSize: 30 },
            columnVisibility: {
              street: false, apt: false, city: false, zip: false,
              taxIdDocumentUrl: false, contactEmail: false, contactPhone: false,
              createdByUserEmail: false
            }
          }}
          state={{ showSkeletons: loading }}
          renderEmptyRowsFallback={() => <Typography sx={{ p: 3, textAlign: "center" }}>No dead checks found.</Typography>}
          renderRowActions={({ row }) => (
            <Button size="small" onClick={() => { setReviveError(""); setSelectedCheck(row.original); }}>Revive</Button>
          )}
          renderTopToolbarCustomActions={({ table }) => {
            const rows = table.getPrePaginationRowModel().rows;
            return (
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2">{rows.length} checks totaling {fCurrency(rows.reduce((sum, row) => sum + (row.original.amount || 0), 0))}</Typography>
                <Tooltip title="Refresh"><IconButton onClick={fetchChecks} disabled={loading}><Iconify icon="eva:refresh-fill" /></IconButton></Tooltip>
                <Tooltip title="Export to csv"><IconButton onClick={() => exportTableToCsv(table, "dead-checks.csv")}><Iconify icon="eva:download-fill" /></IconButton></Tooltip>
              </Stack>
            );
          }}
        />
      </Container>
      <Dialog open={!!selectedCheck} onClose={reviving ? undefined : () => setSelectedCheck(null)} fullWidth maxWidth="xs">
        <DialogTitle>Revive check?</DialogTitle>
        <DialogContent>
          {reviveError && <Alert severity="error" sx={{ mb: 2 }}>{reviveError}</Alert>}
          <Typography>Return check #{selectedCheck?.referenceNumber} for {selectedCheck?.orderNumber} to outstanding checks?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCheck(null)} disabled={reviving}>Cancel</Button>
          <Button onClick={revive} variant="contained" disabled={reviving}>{reviving ? "Reviving..." : "Revive"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
